import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/button/button';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { copyMessageBody } from '../helpers/chatClipboard';
import { MessageTranslationState } from '../hooks/useMessageTranslations';
import {
  Scrim,
  Sheet,
  SheetGrip,
  SheetPreview,
} from './MessageActionSheet.styled';

/**
 * Reply / copy / edit / delete on a touch screen, opened by holding a message.
 *
 * The hover gutter beside a bubble is desktop-only - there is no hover to
 * reveal it with - which left a phone with no way to reply to, edit or delete
 * anything at all. Same actions, same rules about which of them apply. Copy
 * matters most here: the bubbles suppress text selection so press-and-hold
 * can open this sheet at all, so the sheet is the one place a phone can get
 * a message's text from.
 *
 * Rendered once by the thread rather than once per bubble: a sheet per message
 * would be several hundred mounted overlays in a long conversation.
 */

/**
 * Springs up from the bottom edge rather than fading in, so it reads as having
 * come from off screen. Damped hard enough not to wobble - this is a menu, and
 * a menu that bounces looks like it is still settling when you go to tap it.
 */
const SHEET_MOTION = {
  initial: { y: '100%' },
  animate: { y: 0 },
  exit: { y: '100%' },
  transition: { type: 'spring' as const, stiffness: 400, damping: 38 },
};

const SCRIM_MOTION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 },
};

export const MessageActionSheet = ({
  target,
  onClose,
  onReply,
  onEdit,
  onDelete,
  translation,
  onTranslate,
}: {
  /** The message being acted on, and what may be done to it. */
  target: {
    message: ConversationMessageWithStatus;
    canModify: boolean;
  } | null;
  onClose: () => void;
  onReply?: (message: ConversationMessageWithStatus) => void;
  onEdit?: (message: ConversationMessageWithStatus) => void;
  onDelete?: (message: ConversationMessageWithStatus) => void;
  /** The held message's translation, which decides the button's label. */
  translation?: MessageTranslationState | null;
  /** Absent where the held message offers no translation. */
  onTranslate?: (message: ConversationMessageWithStatus) => void;
}) => {
  // Escape closes it rather than leaving whatever is underneath.
  useEffect(() => {
    if (!target) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [target, onClose]);

  const root =
    typeof document === 'undefined'
      ? null
      : document.getElementById('modal-root') ?? document.body;

  if (!root) return null;

  /** Runs an action and closes - every one of them ends this sheet. */
  const pick = (action?: (message: ConversationMessageWithStatus) => void) => {
    const held = target?.message;
    onClose();
    if (held) action?.(held);
  };

  return createPortal(
    /* Both children live inside one AnimatePresence so the sheet can slide
       back down on close instead of vanishing with the state. */
    <AnimatePresence>
      {target && <Scrim key="scrim" {...SCRIM_MOTION} onClick={onClose} />}

      {target && (
        <Sheet key="sheet" role="dialog" aria-label="Message actions" {...SHEET_MOTION}>
          <SheetGrip />
          <SheetPreview>{target.message.body || 'Attachment'}</SheetPreview>

          {onReply && (
            <Button $w="100%" $wSM="100%" onClick={() => pick(onReply)}>
              Reply
            </Button>
          )}

          {/* Null on a deleted message, so the stub offers no copy. */}
          {target.message.body && (
            <Button
              $w="100%"
              $wSM="100%"
              onClick={() =>
                pick((held) => held.body && copyMessageBody(held.body))
              }
            >
              Copy
            </Button>
          )}

          {onTranslate && (
            <Button
              $w="100%"
              $wSM="100%"
              disabled={translation?.status === 'loading'}
              onClick={() => pick(onTranslate)}
            >
              {translation?.status === 'shown' ? 'See original' : 'Translate'}
            </Button>
          )}

          {/* Nothing to edit on a message that is only an attachment. */}
          {target.canModify && onEdit && target.message.body && (
            <Button $w="100%" $wSM="100%" onClick={() => pick(onEdit)}>
              Edit
            </Button>
          )}

          {target.canModify && onDelete && (
            <Button
              $w="100%"
              $wSM="100%"
              $selected
              onClick={() => pick(onDelete)}
            >
              Delete
            </Button>
          )}

          <Button $w="100%" $wSM="100%" $selected $gray onClick={onClose}>
            Cancel
          </Button>
        </Sheet>
      )}
    </AnimatePresence>,
    root
  );
};
